import { ApplyState } from './writeTypes';

/**
 * What counts as an apply that may close the editor.
 *
 * It lives on its own, away from the hook, because the surface reads it on every resolved apply and
 * must be able to do so without pulling the page's axios instance or the durable audit store into
 * whatever is asking.
 *
 * BAR-SHIP-08: `useApply` used to drop the pipeline's result and always resolve, and the modal
 * called `onSave` — the mount's close-and-reload — on every resolved outcome. A stale baseline, an
 * unavailable write log, counts that did not match, a rejected verdict and an outcome nobody
 * observed were all presented through the success path, and each one of them discarded the writer's
 * unsaved script with no dirty-close confirmation in the way.
 */

/**
 * True only for a finished apply whose every chunk reconciled, whose every verdict came back `ok`,
 * and whose every plan closed as `verified` — a status the pipeline reaches only after that chunk's
 * audit entry has been recorded.
 *
 * Everything else is a normal resolved state of the pipeline and is NOT a success.
 */
export function isVerifiedSuccess(state: ApplyState | null | undefined): boolean {
  if (!state || state.phase !== 'done' || state.refusal !== null) {
    return false;
  }

  const chunkKeys = Object.keys(state.verdicts);

  if (chunkKeys.length === 0) {
    return false;
  }

  const everyVerdictPassed = chunkKeys.every((key) => {
    const verdict = state.verdicts[Number(key)];

    return Boolean(verdict) && verdict.ok === true;
  });

  if (!everyVerdictPassed) {
    return false;
  }

  const planIds = Object.keys(state.statuses);

  if (planIds.length === 0) {
    return false;
  }

  return planIds.every((planId) => state.statuses[planId].status === 'verified');
}
